import React, { useEffect, useState } from "react";
import DataTable from "react-data-table-component";
import { fetchFailedInvoices, delete_sanction_order } from "../../../helper/Api";
import { toast } from "react-toastify";
import { BiSolidEdit, BiTrash } from "react-icons/bi";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
} from "@mui/material";
import "./FailedInvoices.css";

const FailedInvoices = ({ setLoading, value }) => {
    const { authData } = useSelector((state) => state.authData);
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [invoices, setInvoices] = useState([]);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [rowPendingDelete, setRowPendingDelete] = useState(null);
    const userTypeLc = String(authData?.user?.user_type || "").toLowerCase();
    const canEditFailedInvoice = userTypeLc === "l1";
    const canDeleteFailedInvoice = userTypeLc === "l3";

    const customStyles = {
        headCells: {
            style: {
                fontWeight: "bold",
                fontSize: "14px",
            },
        },
    };

    const handleChangeRowsPerPage = (currentRowsPerPage) => {
        setRowsPerPage(currentRowsPerPage);
        setCurrentPage(1);
    };

    const getFailedInvoices = async () => {
        try {
            setLoading(true);
            const res = await fetchFailedInvoices();
            if (res.data.status) {
                setInvoices(res.data.result);
            }
        } catch (err) {
            toast.error("Unable to fetch invoices", {
                position: toast.POSITION.TOP_CENTER,
            });
        } finally {
            setLoading(false);
        }
    };

    const openDeleteConfirm = (row) => {
        setRowPendingDelete(row);
        setDeleteConfirmOpen(true);
    };

    const closeDeleteConfirm = () => {
        setDeleteConfirmOpen(false);
        setRowPendingDelete(null);
    };

    const confirmDeleteInvoice = async () => {
        if (!rowPendingDelete) return;
        const row = rowPendingDelete;
        try {
            setLoading(true);
            const res = await delete_sanction_order(row.id);
            if (res?.data?.status) {
                toast.success(res?.data?.message || "Failed invoice deleted.", {
                    position: toast.POSITION.TOP_CENTER,
                });
                setInvoices((prev) => prev.filter((inv) => inv.id !== row.id));
                closeDeleteConfirm();
            } else {
                toast.error(res?.data?.message || "Delete failed.", {
                    position: toast.POSITION.TOP_CENTER,
                });
            }
        } catch (err) {
            toast.error(
                err?.response?.data?.message || err?.message || "Delete failed.",
                {
                    position: toast.POSITION.TOP_CENTER,
                }
            );
        } finally {
            setLoading(false);
        }
    };

    const columns = [
        {
            name: "Sanction Order No",
            selector: (row) => row.id,
            width: "120px",
            minWidth: "120px",
            grow: 0,
        },
        {
            name: "Voucher No",
            selector: (row) => row.voucher_no,
            width: "220px",
            minWidth: "220px",
            grow: 0,
            wrap: true,
        },
        {
            name: "Invoice No.",
            selector: (row) => row.invoice_no,
            width: "180px",
            minWidth: "180px",
            grow: 0,
            wrap: true,
        },
        {
            name: "Voucher Date",
            selector: (row) => row.voucher_date,
            sortable: true,
            width: "150px",
        },
        {
            name: "Beneficiary Name",
            selector: (row) => row.company_name,
            sortable: true,
        },
        {
            name: "Amount",
            selector: (row) => row.payable_amount,
            sortable: true,
            wrap: true,
        },
        {
            name: "Failed Reason",
            selector: (row) => row.reason,
            sortable: true,
            wrap: true,
        },
        {
            name: "Action",
            selector: (row) => (
                <div className="d-flex gap-2 align-items-center justify-content-start flex-wrap">
                    {canEditFailedInvoice && (
                        <Link
                            to={`/edit-failed-invoice/${row.id}`}
                            className="text-decoration-none text-dark"
                            title="Edit"
                        >
                            <BiSolidEdit />
                        </Link>
                    )}
                    {canDeleteFailedInvoice && (
                        <button
                            type="button"
                            className="border-0 bg-transparent p-0 text-danger"
                            title="Delete"
                            onClick={() => openDeleteConfirm(row)}
                            aria-label={`Delete failed invoice ${row.invoice_no || row.id}`}
                        >
                            <BiTrash size={18} />
                        </button>
                    )}
                </div>
            ),
            sortable: false,
            wrap: false,
            width: "110px",
        },
    ];

    useEffect(() => {
        if (value === 0) {
            getFailedInvoices();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    return (
        <>
            <div className="failed-invoices-datatable-root">
                <DataTable
                    customStyles={customStyles}
                    className="dataTables_wrapper"
                    columns={columns}
                    data={invoices}
                    fixedHeader
                    fixedHeaderScrollHeight="600px"
                    pagination
                    paginationPerPage={rowsPerPage}
                    paginationTotalRows={invoices.length}
                    paginationRowsPerPageOptions={[10, 25, 50, 100]}
                    onChangeRowsPerPage={handleChangeRowsPerPage}
                />
            </div>
            <Dialog
                open={deleteConfirmOpen}
                onClose={closeDeleteConfirm}
                maxWidth="xs"
                fullWidth
                aria-labelledby="failed-invoice-delete-title"
            >
                <DialogTitle id="failed-invoice-delete-title">
                    Delete invoice
                </DialogTitle>
                <DialogContent dividers>
                    <p className="mb-2" style={{ fontSize: 15, lineHeight: 1.5 }}>
                        Are you sure you want to delete this invoice? This action cannot be
                        undone.
                    </p>
                    {rowPendingDelete && (
                        <p className="mb-0 text-muted small">
                            Invoice No.:{" "}
                            <strong>{rowPendingDelete.invoice_no ?? "—"}</strong>
                            {rowPendingDelete.voucher_no != null &&
                                rowPendingDelete.voucher_no !== "" && (
                                    <>
                                        {" "}
                                        · Voucher No.:{" "}
                                        <strong>{rowPendingDelete.voucher_no}</strong>
                                    </>
                                )}
                        </p>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 2.5, pb: 2 }}>
                    <Button onClick={closeDeleteConfirm} color="inherit">
                        Cancel
                    </Button>
                    <Button
                        onClick={confirmDeleteInvoice}
                        variant="contained"
                        color="error"
                    >
                        Yes, delete
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default FailedInvoices;
